/**
 * PlaygroundEmbed - Interactive Service Configuration Component
 *
 * Renders the CPQ service-definition playground as a micro-frontend touchpoint.
 * This is the VP-neutral, reusable form of what used to live in
 * `vp-starter/VpStarterTouchpoint` — same MFE embedding behaviour, but decoupled
 * from the vp-starter namespace so a non-VP page (e.g. the Phase-3 Offerten-
 * Generator) can embed the playground without inheriting VP coupling.
 *
 * Features:
 * - Lazy loading with async MFE initialization
 * - Graceful degradation (shows an unavailable message if the MFE is down)
 * - Error boundary to catch touchpoint rendering failures
 * - Retry logic to handle async bootstrap delays
 * - Optional section header with title and description
 */

import React, { Component, type PropsWithChildren, type ReactNode, useEffect, useMemo, useState } from "react";
import { isMfeAvailableAsync } from "../../utils/mfe-app-shell";
import { BrowserFrame } from "../ui/BrowserFrame";

// Dynamically import TouchpointElement to avoid SSG errors
let TouchpointElement: React.ComponentType<{
    frontend: string;
    name: string;
    properties?: Record<string, unknown>;
    className?: string;
}> | null = null;

if (typeof window !== "undefined") {
    import("@qs/app-shell-react")
        .then(module => {
            TouchpointElement = module.TouchpointElement;
        })
        .catch(err => {
            console.warn("[MFE] Failed to load TouchpointElement:", err);
        });
}

/**
 * One selectable service in the multi-service selector. VP-neutral local type —
 * structurally identical to the former `VpStarterTouchpointServiceProps`, so the
 * decoupling from `content/page-vp-starter-home` carries no behaviour change.
 */
export type PlaygroundEmbedService = {
    serviceId: string;
    name: string;
    description: string;
};

/**
 * Component props
 */
export type PlaygroundEmbedProps = PropsWithChildren<{
    /** Service ID to pass to the playground (e.g., "treuhand-service") */
    serviceId: string;
    /** Optional array of services for multi-service selector */
    services?: PlaygroundEmbedService[];
    /** Frontend name (default: "cpq-playground") */
    frontend?: string;
    /** Touchpoint name (default: "service-definition-playground") */
    touchpointName?: string;
    /** Additional properties to pass to the touchpoint element */
    touchpointProperties?: Record<string, unknown>;
    /** HTML id for the section element (used as anchor target) */
    sectionId?: string;
    /** Optional section title */
    title?: string;
    /** Optional section description */
    description?: string;
    /**
     * Ausgang zur oeffentlichen Demo (Endgame § 12). Wird oberhalb des eingebetteten
     * Touchpoints gerendert und bleibt damit sichtbar, auch wenn das MFE nicht laedt.
     */
    demoLink?: {
        text: string;
        href: string;
        hint?: string;
    };
    /** Title shown when MFE is unavailable */
    unavailableTitle?: string;
    /** Message shown when MFE is unavailable */
    unavailableMessage?: string;
    /** Additional CSS classes for the section container */
    sectionClassName?: string;
    /** Additional CSS classes for the top container inside the section */
    containerClassName?: string;
    /** Additional CSS classes for the div wrapping the touchpoint element */
    touchpointClassName?: string;
    /** Whether to wrap the touchpoint in a browser frame (default: true) */
    showBrowserFrame?: boolean;
    /** Background color for the section */
    backgroundColor?: string;
}>;

/**
 * Loading spinner component
 */
const LoadingSpinner: React.FC = () => (
    <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[var(--vps-primary-color)]"></div>
    </div>
);

/**
 * Unavailable message component
 * Shown when the MFE server is not reachable
 */
const UnavailableMessage: React.FC<{ title?: string; message?: string }> = ({ title, message }) => (
    <div className="rounded-lg border-2 border-gray-200 bg-gray-50 p-8 text-center">
        <svg className="mx-auto mb-4 h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
        </svg>
        {title && <p className="text-lg font-medium text-gray-700">{title}</p>}
        {message && <p className="mt-2 text-sm text-gray-500">{message}</p>}
    </div>
);

/**
 * Error boundary to catch touchpoint rendering errors
 * If the touchpoint crashes (e.g., CPQ MFE errors), this boundary catches it
 * and triggers the onError callback to hide the entire section
 */
class TouchpointErrorBoundary extends Component<{ children: ReactNode; onError: () => void }, { hasError: boolean }> {
    constructor(props: { children: ReactNode; onError: () => void }) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): { hasError: boolean } {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        console.warn("[Touchpoint] Rendering error caught:", error, errorInfo);
        this.props.onError();
    }

    render(): ReactNode {
        if (this.state.hasError) {
            return null;
        }
        return this.props.children;
    }
}

/**
 * PlaygroundEmbed Component
 *
 * @example
 * ```tsx
 * <PlaygroundEmbed
 *     serviceId="treuhand-service"
 *     title="Try the Service Configurator"
 *     description="Experience our interactive configuration tool"
 * />
 * ```
 */
export const PlaygroundEmbed: React.FC<PlaygroundEmbedProps> = ({
    serviceId,
    services,
    frontend = "cpq-playground",
    touchpointName = "service-definition-playground",
    touchpointProperties,
    sectionId,
    title,
    description,
    demoLink,
    unavailableTitle,
    unavailableMessage,
    sectionClassName = "",
    containerClassName = "",
    touchpointClassName = "",
    backgroundColor = "bg-neutral-100",
    showBrowserFrame = true,
    children,
}) => {
    const [isMounted, setIsMounted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isAvailable, setIsAvailable] = useState(false);
    const [hasError, setHasError] = useState(false);

    // Service selector state
    const showSelector = (services?.length ?? 0) > 1;
    const defaultServiceId = useMemo(() => services?.[0]?.serviceId ?? serviceId, [services, serviceId]);

    // Build resolved properties for the touchpoint element.
    // When touchpointProperties is set (e.g., onboarding meta-touchpoint), serviceId is passed
    // as a nested JSON `properties` string for the inner touchpoint element.
    // Otherwise, serviceId is passed directly (e.g., cpq-playground).
    const buildTouchpointProps = (svcId: string) => {
        if (touchpointProperties) {
            return {
                ...touchpointProperties,
                properties: JSON.stringify({ serviceId: svcId, showBrowserFrame: !showBrowserFrame }),
            };
        }
        return { serviceId: svcId, showBrowserFrame: !showBrowserFrame };
    };
    const [selectedServiceId, setSelectedServiceId] = useState(defaultServiceId);

    // Reset selection when services/serviceId props change (e.g., branch navigation)
    useEffect(() => {
        setSelectedServiceId(defaultServiceId);
    }, [defaultServiceId]);

    // Track mount state to avoid hydration mismatch
    useEffect(() => {
        setIsMounted(true);
    }, []);

    // Check MFE availability after mount (includes health check)
    useEffect(() => {
        if (!isMounted) {
            return;
        }

        // Async check if MFE is available (includes server health check)
        const checkAvailability = async () => {
            const available = await isMfeAvailableAsync(frontend);
            if (available && TouchpointElement) {
                setIsAvailable(true);
                setIsLoading(false);
            } else {
                // If not available, wait a bit and retry once
                // This handles the case where MFE is still initializing
                setTimeout(() => {
                    isMfeAvailableAsync(frontend)
                        .then(retryAvailable => {
                            setIsAvailable(retryAvailable && TouchpointElement !== null);
                            setIsLoading(false);
                        })
                        .catch(() => {
                            setIsAvailable(false);
                            setIsLoading(false);
                        });
                }, 500);
            }
        };

        checkAvailability().catch(err => {
            console.warn("[Touchpoint] Availability check failed:", err);
            setIsAvailable(false);
            setIsLoading(false);
        });
    }, [isMounted]);

    useEffect(() => {
        let isFirst = true;
        const handleRouteChange = () => {
            if (isFirst) {
                isFirst = false;
                return;
            }
            document.querySelector("#playground-embed")?.scrollIntoView();
        };
        window.addEventListener("cds-cpq-order-request:route-change", handleRouteChange);
        return () => window.removeEventListener("cds-cpq-order-request:route-change", handleRouteChange);
    }, []);

    // Blocker B15: hier stand ein pauschales `return null` bei `!isMounted` — die
    // GANZE Sektion entstand erst nach der Hydration. Folge: Titel, Beschreibung
    // und Demo-Link fehlten im statischen HTML, auf /angebote/ki-anwendungen/offerten-generator
    // und /module/cpq/playground, in DE und EN. Crawler und Antwortmaschinen sahen
    // die Sektion nicht; bei fehlgeschlagener Hydration war sie fuer alle weg.
    //
    // Der Grund war legitim, aber zu grob gezogen: den Hydration-Mismatch verursacht
    // das TouchpointElement (ein Custom Element, das zur Laufzeit geladen wird) —
    // nicht die Ueberschrift, nicht der statische Link. Die Bedingung sitzt jetzt
    // unten am Touchpoint-Block.

    return (
        <section id={sectionId} className={`${sectionClassName} ${backgroundColor}`} data-testid="playground-embed">
            <div className={containerClassName}>
                {/* Optional header */}
                {(title || description) && (
                    <div className="mb-12 text-center">
                        {title && (
                            <h2 className="mb-4 text-3xl font-bold text-[var(--vps-text-primary)] md:text-4xl">
                                {title}
                            </h2>
                        )}
                        {description && (
                            <p className="mx-auto max-w-2xl text-lg text-[var(--vps-text-secondary)]">{description}</p>
                        )}
                    </div>
                )}

                {/* Endgame § 12: der Ausgang zur oeffentlichen Demo.
                    Steht ausserhalb des Header-Blocks und oberhalb des eingebetteten
                    Touchpoints — er soll sichtbar bleiben, egal ob ein Titel gesetzt ist
                    und ob das MFE gerade laedt. Die Ueberschrift verspricht «Probieren Sie
                    es aus»; ohne diesen Link haengt das Versprechen am Ladezustand eines
                    Micro-Frontends. */}
                {demoLink && (
                    <div className="mb-10 text-center" data-testid="playground-demo-link">
                        <a
                            href={demoLink.href}
                            className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 font-semibold
                                text-white transition-colors hover:bg-primary-dark"
                        >
                            {demoLink.text}
                            <span aria-hidden="true">→</span>
                        </a>
                        {demoLink.hint && (
                            <p className="mx-auto mt-3 max-w-2xl text-sm text-[var(--vps-text-secondary)]">
                                {demoLink.hint}
                            </p>
                        )}
                    </div>
                )}

                {/* Service selector teasers (only when multiple services) */}
                {showSelector && services && (
                    <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {services.map(svc => (
                            <button
                                key={svc.serviceId}
                                type="button"
                                onClick={() => setSelectedServiceId(svc.serviceId)}
                                className={`flex flex-col items-start rounded-lg border-2 p-4 text-left transition-all ${
                                    selectedServiceId === svc.serviceId
                                        ? "border-[var(--vps-primary-color)] bg-white shadow-md"
                                        : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                                }`}
                            >
                                <h3 className="text-base font-semibold text-[var(--vps-text-primary)]">{svc.name}</h3>
                                <p className="mt-1 text-sm text-[var(--vps-text-secondary)]">{svc.description}</p>
                            </button>
                        ))}
                    </div>
                )}

                {/* Touchpoint, loading state, or unavailable message.
                    `isMounted` erst hier: vor der Hydration wird der Platz freigelassen,
                    statt die ganze Sektion zu unterdruecken (B15). */}
                {!isMounted ? null : isLoading ? (
                    <LoadingSpinner />
                ) : !isAvailable || hasError ? (
                    <UnavailableMessage title={unavailableTitle} message={unavailableMessage} />
                ) : TouchpointElement ? (
                    <TouchpointErrorBoundary onError={() => setHasError(true)}>
                        {showBrowserFrame ? (
                            <div id="playground-embed">
                                <BrowserFrame url="ihre-website.ch" className={touchpointClassName}>
                                    <TouchpointElement
                                        key={selectedServiceId}
                                        frontend={frontend}
                                        name={touchpointName}
                                        properties={buildTouchpointProps(selectedServiceId)}
                                    />
                                </BrowserFrame>
                            </div>
                        ) : (
                            <div className={touchpointClassName} id="playground-embed">
                                <TouchpointElement
                                    key={selectedServiceId}
                                    frontend={frontend}
                                    name={touchpointName}
                                    properties={buildTouchpointProps(selectedServiceId)}
                                />
                            </div>
                        )}
                    </TouchpointErrorBoundary>
                ) : (
                    <UnavailableMessage title={unavailableTitle} message={unavailableMessage} />
                )}

                {/* Optional children (e.g., CTA buttons) */}
                {children}
            </div>
        </section>
    );
};
